import React, { useMemo } from 'react';
import { Alert, Col, Form, Row, Spinner } from 'react-bootstrap';
import { AnalyticsPeriod, MonitoringAnalyticsResponse } from './types';
import { formatRangeLabel, getBreakdownTotal, getSourceLabel } from './utils';
import { AnalyticsControls, DistributionDonut, SummaryTile } from './Shared';

const getAnalyticsErrorMessage = (error: any, fallback: string) =>
  String(error?.data?.error || error?.data?.message || error?.message || fallback);

export default function OutflowTab({
  analytics,
  isLoading,
  isFetching,
  error,
  period,
  startDate,
  endDate,
  selectedSource,
  selectedPod,
  sources,
  onPeriodChange,
  onStartDateChange,
  onEndDateChange,
  onSourceChange,
  onPodChange,
}: {
  analytics?: MonitoringAnalyticsResponse;
  isLoading: boolean;
  isFetching: boolean;
  error: any;
  period: AnalyticsPeriod;
  startDate: string;
  endDate: string;
  selectedSource: string;
  selectedPod: string;
  sources: string[];
  onPeriodChange: (value: AnalyticsPeriod) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onSourceChange: (value: string) => void;
  onPodChange: (value: string) => void;
}) {
  const outflowSummary = useMemo(
    () => (analytics?.outflowSummary || []).filter(item => item.count > 0),
    [analytics?.outflowSummary],
  );

  const selectedPodBreakdown = useMemo(() => {
    return (
      analytics?.podStatusBreakdown.find(item => item.key === selectedPod)
        ?.values || []
    );
  }, [analytics?.podStatusBreakdown, selectedPod]);

  return (
    <>
      <AnalyticsControls
        period={period}
        startDate={startDate}
        endDate={endDate}
        sources={sources}
        selectedSource={selectedSource}
        showPeriod={false}
        onPeriodChange={onPeriodChange}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onSourceChange={onSourceChange}
      />

      {isLoading ? (
        <div className="adsure-loading-state">
          <Spinner animation="border" className="me-2" />
          Loading execution analytics...
        </div>
      ) : error ? (
        <Alert variant="danger">
          {getAnalyticsErrorMessage(error, 'Failed to load execution analytics.')}
        </Alert>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <SummaryTile
                label="Selected Range"
                value={formatRangeLabel(analytics?.range)}
                tone="#165177"
              />
            </Col>
            <Col md={4}>
              <SummaryTile
                label="Source"
                value={
                  selectedSource === 'all'
                    ? 'All Sources'
                    : getSourceLabel(selectedSource || 'NA')
                }
                tone="#198754"
              />
            </Col>
            <Col md={4}>
              <SummaryTile
                label="Executed Tests"
                value={getBreakdownTotal(outflowSummary)}
                tone="#a50135"
              />
            </Col>
          </Row>

          <Row className="g-3">
            <Col xl={6}>
              <DistributionDonut
                title="Pod Outflow by Executed Status"
                subtitle={`${isFetching ? 'Refreshing live data. ' : ''}Distribution of executed tests by status for the selected source and period.`}
                data={outflowSummary}
                centerLabel="Executed Tests"
              />
            </Col>
            <Col xl={6}>
              <DistributionDonut
                title="Pod Level Analytics"
                subtitle="Status distribution for the selected pod in the chosen period."
                data={selectedPodBreakdown}
                centerLabel={selectedPod || 'Pod'}
                frameCaption={selectedPod || 'Pod'}
                controls={
                  <Form.Select
                    value={selectedPod}
                    onChange={event => onPodChange(event.target.value)}
                  >
                    {(analytics?.availablePods || []).map(pod => (
                      <option key={pod} value={pod}>
                        {pod}
                      </option>
                    ))}
                  </Form.Select>
                }
                showCenterLabel={false}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
}
